import { useEffect, useRef, useState, useCallback } from "react";
import { agencyAuth } from "./identity";

/**
 * `useAgencyFirehose` — replaces the old 60 s polling loop with a
 * real-time WebSocket. The agency dispatcher socket subscribes to TWO
 * rooms server-side:
 *   • `agency:<emergency_type>` — broad feed of new alerts for this type
 *   • `agency-id:<uuid>`        — TARGETED dispatch + operational events
 *
 * Auto-reconnects with capped exponential backoff (1 → 30 s) so a
 * dispatcher leaving the tab open overnight never silently loses alerts.
 *
 * Returns: `{ live, connecting }`. `connecting === true` means the
 * socket dropped and we are mid-reconnect (UI shows "Reconnexion…").
 *
 *   const { live, connecting } = useAgencyFirehose({
 *     enabled: !!session,
 *     onAlertCreated:        (alert)          => { ... },
 *     onAlertDispatched:     (alert)          => { ... },
 *     onAlertOperational:    (alert)          => { ... },
 *     onInterventionUpdate:  (alertId, iv)    => { ... },
 *   });
 *
 * Note: the WS picks up the rr_agency_token httpOnly cookie set by
 * /api/agency/login automatically — no query-string token, no
 * Authorization header, no JS-visible secret.
 */
export function useAgencyFirehose({
  enabled = true,
  onAlertCreated,
  onAlertDispatched,
  onAlertOperational,
  onInterventionUpdate,
  onCitizenAmended,
  onLocationUpdated,
} = {}) {
  const [live, setLive] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);
  const closedRef = useRef(false);
  const cbCreated = useRef(onAlertCreated);
  const cbDispatched = useRef(onAlertDispatched);
  const cbOperational = useRef(onAlertOperational);
  const cbIntervention = useRef(onInterventionUpdate);
  const cbAmended = useRef(onCitizenAmended);
  const cbLocation = useRef(onLocationUpdated);
  useEffect(() => { cbCreated.current = onAlertCreated; }, [onAlertCreated]);
  useEffect(() => { cbDispatched.current = onAlertDispatched; }, [onAlertDispatched]);
  useEffect(() => { cbOperational.current = onAlertOperational; }, [onAlertOperational]);
  useEffect(() => { cbIntervention.current = onInterventionUpdate; }, [onInterventionUpdate]);
  useEffect(() => { cbAmended.current = onCitizenAmended; }, [onCitizenAmended]);
  useEffect(() => { cbLocation.current = onLocationUpdated; }, [onLocationUpdated]);

  const ackAlertId = useCallback(() => {
    // No-op placeholder — agencies acknowledge via the existing PATCH
    // intervention API. Kept as a stable identity so a future feature
    // (e.g. WS-only ack) has a clear extension point.
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!agencyAuth.isLoggedIn()) return undefined;
    closedRef.current = false;

    const buildUrl = () => {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const wsBase = base.replace(/^http/i, "ws");
      return `${wsBase}/api/ws/agency/firehose`;
    };

    const open = () => {
      if (closedRef.current) return;
      setConnecting(true);
      let ws;
      try { ws = new WebSocket(buildUrl()); }
      catch (_e) { schedule(); return; }
      wsRef.current = ws;
      ws.onopen = () => {
        // Wait for the first hello frame before flipping LIVE — this
        // avoids the brief OFF→OFF→ON visual flicker on initial connect
        // when WebSocket onopen fires before the server has sent its
        // welcome envelope.
        retryRef.current = 0;
      };
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); }
        catch { return; }
        if (!msg || !msg.type) return;
        if (msg.type === "hello") {
          // First server-sent frame after a successful auth handshake —
          // safe moment to flip the indicator to LIVE.
          setLive(true);
          setConnecting(false);
          return;
        }
        if (msg.type === "alert.created" && msg.alert) {
          if (cbCreated.current) {
            try { cbCreated.current(msg.alert); } catch (e) { void e; }
          }
        } else if (msg.type === "alert.dispatched.to-you" && msg.alert) {
          // TARGETED dispatch from Central Admin — this is the event
          // that makes new dispatched alerts appear instantly.
          if (cbDispatched.current) {
            try { cbDispatched.current(msg.alert); } catch (e) { void e; }
          }
        } else if (msg.type === "alert.operational" && msg.alert) {
          // Operational status change pushed by either this dispatcher's
          // tab or a teammate's tab on the same agency.
          if (cbOperational.current) {
            try { cbOperational.current(msg.alert); } catch (e) { void e; }
          }
        } else if (msg.type === "intervention.update" && msg.alert_id) {
          if (cbIntervention.current) {
            try { cbIntervention.current(msg.alert_id, msg.intervention); }
            catch (e) { void e; }
          }
        } else if (msg.type === "alert.citizen_amended" && msg.alert_id) {
          // Citizen submitted an amendment AFTER dispatch — backend
          // only sends this to the targeted `agency-id:<uuid>` room
          // for the currently assigned agencies, never to unassigned
          // ones. We just forward to the dashboard which decides how
          // to surface (toast + force-reload of the case file).
          if (cbAmended.current) {
            try { cbAmended.current(msg.alert_id, msg.amendment); }
            catch (e) { void e; }
          }
        } else if (msg.type === "alert.location_updated" && msg.alert_id) {
          // Iter90 — GPS audit: post-SOS location refinement for an
          // alert assigned to this agency. Published to BOTH the
          // broad `agency:<type>` room and the targeted
          // `agency-id:<uuid>` room once dispatched — sync continues
          // across dispatch, as required.
          if (cbLocation.current) {
            try { cbLocation.current(msg.alert_id, msg); }
            catch (e) { void e; }
          }
        }
      };
      ws.onclose = () => {
        setLive(false);
        if (!closedRef.current) {
          setConnecting(true);
          schedule();
        }
      };
      ws.onerror = () => {
        try { ws.close(); }
        catch (e) { /* noop */ void e; }
      };
    };

    const schedule = () => {
      const delay = Math.min(1000 * Math.pow(2, retryRef.current), 30000);
      retryRef.current += 1;
      timerRef.current = setTimeout(open, delay);
    };

    open();

    return () => {
      closedRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (wsRef.current) {
        const sock = wsRef.current;
        // Guard against close-before-open: WebSocket throws
        // InvalidStateError in some browsers if close() is called while
        // readyState === 0. We silently ignore — the in-flight connect
        // will resolve and the onclose path is short-circuited by
        // closedRef.current = true above.
        try {
          if (sock.readyState === WebSocket.CONNECTING) {
            sock.onopen = () => { try { sock.close(); } catch (e) { /* noop */ void e; } };
          } else {
            sock.close();
          }
        } catch (e) { /* noop */ void e; }
      }
    };
  }, [enabled]);

  return { live, connecting, ackAlertId };
}
